import { readExclusiveLoginRecord, writeExclusiveLoginRecord } from "./exclusiveLoginStorage";

/**
 * 같은 오리진 탭 사이의 로그인 사건 신호 (design D8).
 *
 * localStorage 키 하나에 가장 최근 사건 `{type, at}` 만 둔다. 다른 탭은 `storage` 이벤트로 받고, 멈춰 있어
 * 이벤트를 놓친 탭은 다시 보일 때 키를 읽는다. 한 키가 전파와 재확인을 겸한다.
 *
 * - `superseded`: 밀려남을 처리한 탭이 쓴다. 받은 탭은 자기 밀려남 처리를 한다(재전파 없음).
 * - `signed-out`: 명시적 로그아웃이 해제보다 먼저 쓴다.
 * - `signed-in`: 점유가 진행으로 끝나면 쓴다. 늦게 깨어난 탭이 그보다 오래된 사건을 적용하지 않게 한다.
 */
const LOGIN_TAB_SIGNAL_KEY = "exclusiveLogin.tabSignal";
const LOGIN_TAB_SIGNAL_TYPES = ["superseded", "signed-out", "signed-in"] as const;

export type LoginTabSignalType = (typeof LOGIN_TAB_SIGNAL_TYPES)[number];
export type LoginTabSignal = { type: LoginTabSignalType; at: number };

/**
 * 이 탭이 이미 따른 사건의 시각 경계. 페이지 로드 시각에서 시작한다 — 로드 전에 생긴 사건은 이 탭이 따를
 * 대상이 아니다. 이 탭이 직접 쓴 사건과 구독자에게 전달한 사건이 경계를 올린다.
 */
let appliedUntil = Date.now();
const listeners = new Set<(signal: LoginTabSignal) => void>();

const isLoginTabSignalType = (value: unknown): value is LoginTabSignalType =>
  LOGIN_TAB_SIGNAL_TYPES.includes(value as LoginTabSignalType);

function parseLoginTabSignal(raw: string | null): LoginTabSignal | null {
  if (raw === null) return null;
  try {
    const parsed = JSON.parse(raw) as Partial<LoginTabSignal> | null;
    if (isLoginTabSignalType(parsed?.type) && typeof parsed?.at === "number") {
      return { type: parsed.type, at: parsed.at };
    }
  } catch {
    // 형식이 깨진 기록은 사건이 아니다
  }
  return null;
}

/** 사건을 기록한다. 이 탭이 쓴 사건은 이 탭에서 이미 처리된 것으로 본다. */
export function writeLoginTabSignal(type: LoginTabSignalType): void {
  const signal: LoginTabSignal = { type, at: Date.now() };
  appliedUntil = Math.max(appliedUntil, signal.at);
  writeExclusiveLoginRecord(LOGIN_TAB_SIGNAL_KEY, JSON.stringify(signal));
}

/** 가장 최근 사건. 기록이 없거나 형식이 깨졌으면 null 이다. */
export function readLoginTabSignal(): LoginTabSignal | null {
  return parseLoginTabSignal(readExclusiveLoginRecord(LOGIN_TAB_SIGNAL_KEY));
}

/**
 * 이 탭이 아직 따르지 않은 사건인가. 로드 전·이미 따른 사건은 아니다. 저장된 최신 사건보다 오래된 사건도
 * 아니다 — 멈춰 있던 탭에 늦게 도착한 낡은 이벤트(예: `signed-in` 이 덮어쓴 `superseded`)가 새 로그인을
 * 지우지 않게 한다.
 */
function isUnappliedSignal(signal: LoginTabSignal): boolean {
  if (signal.at <= appliedUntil) return false;
  const latest = readLoginTabSignal();
  return latest === null || latest.at <= signal.at;
}

function deliverIfUnapplied(signal: LoginTabSignal | null): void {
  if (!signal || !isUnappliedSignal(signal)) return;
  appliedUntil = signal.at;
  listeners.forEach((listener) => listener(signal));
}

const handleStorage = (event: StorageEvent) => {
  if (event.key !== LOGIN_TAB_SIGNAL_KEY) return;
  deliverIfUnapplied(parseLoginTabSignal(event.newValue));
};

const handleVisibilityChange = () => {
  if (document.visibilityState === "visible") deliverIfUnapplied(readLoginTabSignal());
};

/**
 * 이 탭이 아직 따르지 않은 다른 탭의 사건을 구독한다. 두 경로로 받는다: 다른 탭이 쓸 때의 `storage` 이벤트,
 * 그리고 이 탭이 다시 보일 때의 최신 기록 재확인. 각 사건은 한 번만 전달된다. 반환 함수로 구독을 끊는다.
 */
export function subscribeLoginTabSignal(listener: (signal: LoginTabSignal) => void): () => void {
  if (listeners.size === 0) {
    window.addEventListener("storage", handleStorage);
    document.addEventListener("visibilitychange", handleVisibilityChange);
  }
  listeners.add(listener);

  return () => {
    listeners.delete(listener);
    if (listeners.size === 0) {
      window.removeEventListener("storage", handleStorage);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    }
  };
}
